// ArabicModern - Arabic Modern Standard
// Histoire : Plateau TV - debat economique en arabe

import type {
  Character,
  ScenarioChapter,
  SequenceItem,
  StoryContent,
  ChatContent,
  CardContent,
  QuizContent,
  TaskContent,
} from './storyLoader';

export interface ArabicModernScenario {
  id: string;
  title: string;
  subtitle: string;
  certification: string;
  coverImage: ReturnType<typeof require>;
  characters: Character[];
  chapters: ScenarioChapter[];
}

const arabicModernCharacters: Character[] = [
  { id: 'tarek',   name: 'Tarek',   role: 'Language Director',              color: '#1E3A5F', avatar: 'tarek'   },
  { id: 'leila',   name: 'Leila',   role: 'Cultural Linguist',              color: '#065F46', avatar: 'leila'   },
  { id: 'natasha', name: 'Natasha', role: 'International Business Consultant', color: '#7C2D12', avatar: 'natasha' },
  { id: 'viktor',  name: 'Viktor',  role: 'Diplomatic Interpreter',         color: '#4338CA', avatar: 'viktor'  },
];

const scene1: SequenceItem = {
  type: 'story',
  id: 'am-scene-1',
  title: "Scene 1 : Plateau TV - Debat economique",
  duration: 5,
  content: {
    narrativeText:
      "Plateau de television, Le Caire. Les lumieres s'allument.\n\n" +
      "**Tarek :** \"السيدات والسادة، مرحباً بكم في برنامج الاقتصاد والمال. موضوع اليوم: التحول الرقمي في منطقة الشرق الأوسط وشمال أفريقيا.\"\n(Mesdames et messieurs, bienvenue dans l'emission Economie et Finance. Le sujet d'aujourd'hui : la transformation numerique dans la region MENA.)\n\n" +
      "**Natasha (en francais) :** \"Viktor, il utilise des termes que je n'ai pas vus dans mes cours. 'التحول الرقمي' — c'est quoi exactement ?\"\n\n" +
      "**Viktor :** \"'التحول الرقمي' (at-tahawwul ar-raqami) : la transformation numerique. 'الرقمي' vient de 'رقم' (chiffre/numero). C'est l'arabe standard moderne pour 'digital'.\"\n\n" +
      "**Tarek :** \"الاقتصاد الرقمي يشكل الآن 15% من الناتج المحلي الإجمالي في المنطقة، وهو في ازدياد مستمر.\"\n(L'economie numerique represente maintenant 15% du PIB regional et est en croissance continue.)\n\n" +
      "**Leila :** \"Notez comme Tarek utilise 'الناتج المحلي الإجمالي' pour PIB — c'est le terme officiel dans tous les documents economiques arabes formels.\"\n\n" +
      "**Natasha :** \"Et 'الشرق الأوسط' pour Moyen-Orient — mais comment dit-on 'disruption' en arabe ?\"\n\n" +
      "**Leila :** \"'اضطراب' (idtirab) ou parfois 'تعطيل' (ta'til) selon le contexte. En economie, on entend aussi l'anglicisme 'ديسرابشن' dans les milieux d'affaires.\"\n\n" +
      "**Viktor :** \"C'est la tension entre l'arabe standard pur et les emprunts. Dans les negociations, toujours privilegier le terme arabe officiel.\"",
    characters: ['tarek', 'leila', 'natasha', 'viktor'],
  } as StoryContent,
};

const card1: SequenceItem = {
  type: 'card',
  id: 'am-card-1',
  title: 'Vocabulaire : المصطلحات الاقتصادية (Vocabulaire economique)',
  duration: 6,
  content: {
    title: 'المصطلحات الاقتصادية — Vocabulaire economique en arabe standard',
    body:
      "## المصطلحات الاقتصادية\n\n" +
      "### Termes fondamentaux\n\n" +
      "| Arabe | Translitteration | Francais |\n" +
      "|---|---|---|\n" +
      "| الاقتصاد | al-iqtisad | L'economie |\n" +
      "| الناتج المحلي الإجمالي | an-natij al-mahalli al-ijmali | PIB |\n" +
      "| التضخم | at-tadakhkhum | L'inflation |\n" +
      "| الاستثمار | al-istithmar | L'investissement |\n" +
      "| الميزانية | al-mizaniyya | Le budget |\n" +
      "| التجارة | at-tijara | Le commerce |\n" +
      "| الصادرات | as-sadirat | Les exportations |\n" +
      "| الواردات | al-waridat | Les importations |\n\n" +
      "### Economie numerique\n\n" +
      "| Arabe | Francais |\n" +
      "|---|---|\n" +
      "| التحول الرقمي | Transformation numerique |\n" +
      "| الاقتصاد الرقمي | Economie numerique |\n" +
      "| الذكاء الاصطناعي | Intelligence artificielle |\n" +
      "| البيانات الضخمة | Big Data |\n" +
      "| الأمن السيبراني | Cybersecurite |\n\n" +
      "### Structure grammaticale\n" +
      "L'arabe standard moderne (الفصحى) utilise l'annexion ('idafa') pour les termes composes :\n" +
      "- الاقتصاد + الرقمي = الاقتصاد الرقمي (economie numerique)\n" +
      "- L'adjectif suit le nom et s'accorde en genre, nombre et determination.\n\n" +
      "### Differences dialectale vs standard\n" +
      "Dans les affaires, l'arabe standard (MSA) est toujours prefere aux dialectes pour les documents et presentations formelles.",
  } as CardContent,
};

const chat1: SequenceItem = {
  type: 'chat',
  id: 'am-chat-1',
  title: 'Questions de comprehension en arabe',
  duration: 6,
  content: {
    tutorCharacterId: 'leila',
    messages: [
      {
        id: 'am-chat-1-q1',
        sender: 'character',
        characterId: 'leila',
        text: "Comment dit-on 'transformation numerique' en arabe standard ?",
        type: 'single-choice',
        options: [
          "التحول الرقمي (at-tahawwul ar-raqami)",
          "الاقتصاد الحديث (al-iqtisad al-hadith)",
          "التقنية المالية (at-taqniyya al-maliyya)",
          "الثورة الصناعية (ath-thawra as-sina'iyya)",
        ],
        correctAnswer: "التحول الرقمي (at-tahawwul ar-raqami)",
        explanation: "التحول (at-tahawwul) = transformation/transition. الرقمي (ar-raqami) = numerique/digital, derive de رقم (chiffre). C'est le terme officiel utilise dans les documents gouvernementaux et economiques.",
      },
      {
        id: 'am-chat-1-q2',
        sender: 'character',
        characterId: 'leila',
        text: "Dans la phrase 'الناتج المحلي الإجمالي يشكل 15%', quel est le verbe principal ?",
        type: 'single-choice',
        options: [
          "الناتج (an-natij) — le produit",
          "يشكل (yushakkil) — il forme/represente",
          "المحلي (al-mahalli) — local",
          "الإجمالي (al-ijmali) — total",
        ],
        correctAnswer: "يشكل (yushakkil) — il forme/represente",
        explanation: "يشكل est la forme du verbe شكّل (shakkala, former/constituer) a la 3eme personne du singulier masculin. En arabe, les verbes s'accordent en genre et nombre avec leur sujet.",
      },
      {
        id: 'am-chat-1-q3',
        sender: 'character',
        characterId: 'leila',
        text: "Quelle est la bonne traduction de 'L'intelligence artificielle dans le secteur bancaire' ?",
        type: 'single-choice',
        options: [
          "الذكاء الاصطناعي في القطاع المصرفي",
          "الذكاء الاصطناعي عن القطاع المصرفي",
          "الذكاء الاصطناعي من القطاع المصرفي",
          "الذكاء الاصطناعي بالقطاع المصرفي",
        ],
        correctAnswer: "الذكاء الاصطناعي في القطاع المصرفي",
        explanation: "في (fi) = dans/en. C'est la preposition correcte pour exprimer la localisation ou le domaine. عن = a propos de, من = de/depuis, ب = avec/par.",
      },
    ],
  } as ChatContent,
};

const task1: SequenceItem = {
  type: 'task',
  id: 'am-task-1',
  title: 'Rediger un rapport economique en arabe standard',
  duration: 40,
  content: {
    platform: 'Traitement de texte / Document Arabic',
    objective: "Produire un rapport de 2 paragraphes sur la transformation numerique en arabe standard",
    steps: [
      {
        stepNumber: 1,
        action: "Structure du rapport",
        detail: "Titre : تقرير: التحول الرقمي في منطقة الشرق الأوسط. Introduction (3 phrases). Corps (5 phrases avec statistiques). Conclusion (2 phrases).",
      },
      {
        stepNumber: 2,
        action: "Utiliser le vocabulaire cle",
        detail: "Integrer : الاقتصاد الرقمي، الذكاء الاصطناعي، الناتج المحلي الإجمالي، التجارة الإلكترونية، الاستثمار الأجنبي",
      },
      {
        stepNumber: 3,
        action: "Structure des phrases arabes",
        detail: "Commencer avec une phrase nominale : الاقتصاد الرقمي هو... Puis verbale : يشهد القطاع نمواً... Utiliser les conjonctions : و (et), لذلك (donc), ومع ذلك (cependant).",
      },
      {
        stepNumber: 4,
        action: "Relecture et correction",
        detail: "Verifier les accords (genre/nombre), les voyelles longues, la correction de l'article defini (ال) devant les lettres solaires et lunaires.",
      },
    ],
    validation: "Rapport de 150-200 mots. Vocabulaire economique correct. Grammaire MSA verifiee par Leila.",
  } as TaskContent,
};

const quiz1: SequenceItem = {
  type: 'quiz',
  id: 'am-quiz-1',
  title: 'Quiz : Vocabulaire et grammaire',
  duration: 5,
  content: {
    questions: [
      {
        id: 'am-q1-1',
        question: "Quelle est la traduction correcte de 'les exportations petrolieres' ?",
        type: 'single-choice',
        options: [
          "الصادرات النفطية (as-sadirat an-naftiyya)",
          "الواردات النفطية (al-waridat an-naftiyya)",
          "الاستثمار النفطي (al-istithmar an-nafti)",
          "التجارة النفطية (at-tijara an-naftiyya)",
        ],
        correctAnswer: "الصادرات النفطية (as-sadirat an-naftiyya)",
        explanation: "الصادرات (as-sadirat) = les exportations (pluriel feminin). النفطية (an-naftiyya) = petroliere (adjectif feminin accord avec le nom). الواردات = importations.",
      },
      {
        id: 'am-q1-2',
        question: "Comment dit-on 'investissement etranger' en arabe standard ?",
        type: 'single-choice',
        options: [
          "الاستثمار الأجنبي (al-istithmar al-ajnabi)",
          "التجارة الأجنبية (at-tijara al-ajnabiyya)",
          "الاقتصاد الدولي (al-iqtisad ad-dawli)",
          "المال الأجنبي (al-mal al-ajnabi)",
        ],
        correctAnswer: "الاستثمار الأجنبي (al-istithmar al-ajnabi)",
        explanation: "الاستثمار (al-istithmar) = investissement. الأجنبي (al-ajnabi) = etranger (masculin singulier, s'accorde avec استثمار). La structure nom + adjectif est fondamentale en arabe.",
      },
      {
        id: 'am-q1-3',
        question: "Dans quel contexte utilise-t-on l'arabe standard moderne (MSA) plutot qu'un dialecte ?",
        type: 'multiple-choice',
        options: [
          "Documents officiels et rapports d'entreprise",
          "Negociations diplomatiques formelles",
          "Conversations informelles entre collegues",
          "Presentations academiques et conferences",
        ],
        correctAnswer: [
          "Documents officiels et rapports d'entreprise",
          "Negociations diplomatiques formelles",
          "Presentations academiques et conferences",
        ],
        explanation: "Le MSA (الفصحى) est utilise dans tous les contextes formels : medias officiels, documents legaux, academie, diplomatie. Les conversations informelles utilisent les dialectes regionaux (egyptien, levantin, maghrebin, etc.).",
      },
    ],
  } as QuizContent,
};

const chapter1: ScenarioChapter = {
  id: 'chapter-1',
  title: 'في قلب الحدث — Au coeur de l evenement',
  subtitle: 'Arabe Standard Moderne — Economie et diplomatie',
  learningObjectives: [
    "Maitriser le vocabulaire economique en arabe standard",
    "Comprendre la structure grammaticale MSA",
    "Distinguer arabe standard des dialectes",
    "Rediger un rapport economique en MSA",
    "Naviguer les termes techniques dans un contexte professionnel",
  ],
  sequence: [scene1, card1, chat1, task1, quiz1],
};

export const arabicModernScenario: ArabicModernScenario = {
  id: 'arabic-modern',
  title: 'Arabic Modern',
  subtitle: "Au coeur de l'economie mondiale — en arabe",
  certification: 'Arabic Modern Standard',
  coverImage: require('../../../assets/images/characters/nina_raoul_working.png'),
  characters: arabicModernCharacters,
  chapters: [chapter1],
};

export default arabicModernScenario;
